import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { LeadDashboardResolve } from './lead-dashboard-resolver.service';
import { RouteInformationService } from './route-information.service';
import { Lead } from '../lead.model';
import { LeadService } from '../services/leads/lead.service';
import {
  ExaminationResult,
  TOO_FAR_REASONS,
  TooFarReason,
} from '../examinationResult.model';
import { GeolocationService } from '../services/geolocation/geolocation.service';
import { DialogService } from '../services/dialog/dialog.service';

@Injectable()
export class LeadDashboardService {
  private readonly currentExamination = new BehaviorSubject<
    { lead: Lead; draft: ExaminationResult } | undefined
  >(undefined);
  public readonly currentExamination$ = this.currentExamination.asObservable();

  constructor(
    route: ActivatedRoute,
    private routeInformationService: RouteInformationService,
    private leadService: LeadService,
    private geolocationService: GeolocationService,
    private dialogService: DialogService,
  ) {
    const leadTask: LeadDashboardResolve =
      route.snapshot.data['leadDashboardResolve'];
    if (leadTask.draft !== undefined) {
      const lead = this.leadService.getLead(leadTask.draft.id);
      if (lead !== undefined) {
        this.currentExamination.next({ lead: lead, draft: leadTask.draft });
      }
    }
  }

  public startLeadExaminationWithStartingLogs(
    lead: Lead,
    latitude: number,
    longitude: number,
    reason: TooFarReason | undefined = undefined,
  ): void {
    this.currentExamination.next({
      lead: lead,
      draft: {
        id: lead.id,
        startTimeIso8601: new Date().toISOString(),
        startCoordinates: {
          latitude: latitude,
          longitude: longitude,
        },
        startReason: reason,
        roomExaminations: [],
        selfCreatedRooms: [],
        images: [],
        playbackDevicesVisible: false,
        playbackDevicesLocation: null,
        guestsPresent: false,
        guestCount: null,
      },
    });
  }

  public async startLeadExamination(lead: Lead) {
    const isCloseEnough =
      await this.geolocationService.currentDistanceFromPositionCloseEnough(
        lead.coordinates,
      );
    const currentPosition = await this.geolocationService.getPosition();
    if (isCloseEnough) {
      this.startLeadExaminationWithStartingLogs(
        lead,
        currentPosition.coords.latitude,
        currentPosition.coords.longitude,
      );
    } else {
      this.dialogService.openSelectDialog(
        TOO_FAR_REASONS.map((reason) => {
          return {
            option: reason,
            label: 'enums.too_far_reason.' + reason,
          };
        }),
        'components.selected-lead-list.too-far-label',
        'components.selected-lead-list.too-far-text',
        (reason) => {
          this.startLeadExaminationWithStartingLogs(
            lead,
            currentPosition.coords.latitude,
            currentPosition.coords.longitude,
            reason as TooFarReason,
          );
        },
        'components.selected-lead-list.too-far-headline',
      );
    }
  }

  public getCurrentLead(): Lead {
    return this.currentExamination.value!.lead;
  }

  public getCurrentDraft(): ExaminationResult {
    return this.currentExamination.value!.draft;
  }

  public finishExamination(): void {
    this.routeInformationService.updateTour(
      this.currentExamination.value!.lead,
    );
    this.currentExamination.next(undefined);
  }
}
